<template>
  <li class="location-upload-row" :class="{ 'location-upload-row--drag-over': isDragOver }" @dragover.prevent="handleDragOver" @dragleave="handleDragLeave" @drop.prevent="handleDrop">
    <div class="location-upload-row__main">
      <LocationAvatar v-if="props.row.location" :src="props.row.location.icon" :name="props.row.location.name" :size="44" />
      <span v-else class="location-upload-row__album-avatar" aria-hidden="true">
        <IconEmbedded name="folder_2" :size="22" color="currentColor" />
      </span>

      <div class="location-upload-row__copy">
        <div class="location-upload-row__name-row">
          <span class="location-upload-row__name">{{ props.row.title }}</span>
          <LocationCategoryIcon v-if="props.row.location" :tagKind="props.row.location.tagKind" :category="props.row.location.category" :size="24" />

          <!-- Album options. A location with no album yet has nothing to edit or delete. -->
          <SrpDropdown
            v-if="props.row.gallery"
            class="location-upload-row__album-menu"
            :class="{ 'location-upload-row__album-menu--disabled': isAlbumMenuDisabled }"
            placement="bottom-start"
            :offset="4"
            :width="170"
            :isMobileModalCloseButtonVisible="false"
          >
            <template #clickableElement="{ isDropdownVisible }">
              <button type="button" class="location-upload-row__gear" title="Album options" :disabled="isAlbumMenuDisabled" :aria-label="`Album options for ${props.row.title}`" :aria-expanded="isDropdownVisible">
                <IconEmbedded name="gear_2" :size="16" color="#667085" />
              </button>
            </template>
            <template #dropdown="{ close, isDropdownVisible }">
              <div class="location-upload-row__album-menu-list" role="menu" :inert="!isDropdownVisible" :aria-hidden="!isDropdownVisible" @keydown.esc.stop="close()">
                <button v-if="canAddLocation" type="button" role="menuitem" @click="chooseEditAlbum(true, close)">Add location</button>
                <button type="button" role="menuitem" @click="chooseEditAlbum(false, close)">Edit album</button>
                <button type="button" role="menuitem" class="location-upload-row__album-menu-delete" @click="chooseDeleteAlbum(close)">Delete album</button>
              </div>
            </template>
          </SrpDropdown>
        </div>

        <div class="location-upload-row__meta">
          <span v-if="locationLabel" class="location-upload-row__location">
            <IconEmbedded name="map-pin_2" :size="14" color="#667085" />
            {{ locationLabel }}
          </span>
          <span>{{ statusText }}</span>
          <button v-if="photoCount > 0" type="button" class="location-upload-row__link" :aria-expanded="isExpanded" @click="isExpanded = !isExpanded">
            {{ isExpanded ? "hide" : "show" }}
          </button>
        </div>
      </div>

      <!-- Mouse shortcut for show/hide; the link above is the accessible control. -->
      <ul v-if="previewPhotoIds.length" class="location-upload-row__previews" role="presentation" @click="isExpanded = !isExpanded">
        <li v-for="(photoId, index) in previewPhotoIds" :key="photoId" class="location-upload-row__preview">
          <SrpFileThumbnail class="location-upload-row__preview-image" :fileName="photoId" thumbSize="thumb-tiny" imageFit="cover" :isWithLoader="false" />
          <span v-if="morePreviewCount > 0 && index === previewPhotoIds.length - 1" class="location-upload-row__preview-more">+{{ morePreviewCount.toLocaleString("en-US") }}</span>
        </li>
      </ul>
      <span v-else class="location-upload-row__drop-hint">
        <IconEmbedded name="image_2" :size="17" color="currentColor" />
        {{ isDragOver ? "Drop to upload" : "Drop photos here" }}
      </span>

      <SrpButton
        class="location-upload-row__upload-button"
        tag="button"
        type="button"
        color="teal"
        fill="outlined"
        sidePaddings="small"
        :isDisabled="!canUpload"
        :disabled="!canUpload"
        :aria-label="`Upload photos to ${props.row.title}`"
        @click="openFilePicker"
      >
        <template #icon>
          <LoadingIcon v-if="isUploadingInProgress" :size="17" />
          <IconEmbedded v-else name="upload_3" :size="17" />
        </template>
        {{ isUploadingInProgress ? "Uploading" : "Upload Photos" }}
      </SrpButton>
    </div>

    <!-- Does the uploading; the row above is its only UI. -->
    <div class="location-upload-row__upload-form" aria-hidden="true">
      <UploadPhotoForm
        ref="uploadPhotoFormRef"
        callerName="atlasUploadByLocation"
        flow="customer-upload"
        :ownerId="props.customerId"
        :img="NO_IMAGES"
        :isThumbnailsVisible="false"
        :maxNumberOfFilesToUploadAtOnce="maxAssetsPerGroup"
        :beforeUploadCheck="checkAssetLimitBeforeUpload"
        hideVideoGuidance
        :isVideoUploadAllowed="props.isVideoUploadAllowed"
        :inputId="uploadInputId"
        @uploadingStarted="handleUploadingStarted"
        @imageUploadedToServer="handleImageUploaded"
        @uploadProgressChange="handleProgress"
      />
    </div>

    <div v-if="isExpanded && photoCount > 0" class="location-upload-row__details">
      <div class="location-upload-row__actions">
        <template v-if="props.isSelectMode">
          <SrpCheckbox size="medium" @click.prevent="toggleSelectAll">
            <template #input>
              <input type="checkbox" :checked="isAllChecked" style="pointer-events: none" />
            </template>
            <template #text>Select all</template>
          </SrpCheckbox>
          <LinkWithIcon isDottedUnderline color="teal" @click="emit('stopSelecting')">
            <template #icon><IconEmbedded name="check_4" :size="15" /></template>
            Done Moving/Deleting
          </LinkWithIcon>
        </template>
        <LinkWithIcon v-else isDottedUnderline color="teal" :isDisabled="props.areAlbumActionsDisabled" @click="emit('startSelecting')">
          <template #icon><IconEmbedded name="pencil_2-5" :size="16" /></template>
          Move/Delete Photos
        </LinkWithIcon>
      </div>

      <p v-if="albumDescription" class="location-upload-row__description">{{ albumDescription }}</p>
      <p v-if="props.row.gallery?.photoCredit" class="location-upload-row__description">Photo Credit: {{ props.row.gallery.photoCredit }}</p>

      <ul class="location-upload-row__thumbnails">
        <li v-for="photoId in visiblePhotoIds" :key="photoId" class="location-upload-row__thumbnail">
          <SrpFileThumbnail
            :fileName="photoId"
            thumbSize="thumb-tiny"
            :galleryPostfix="lightboxKey"
            :isEntireAreaClickable="!props.isSelectMode"
            isEntireAreaCheckable
            :isWithLoader="false"
          >
            <template v-if="props.isSelectMode" #default>
              <input type="checkbox" :checked="props.checkedPhotoIds.includes(photoId)" @change="$event => checkUncheckPhoto($event, photoId)" />
            </template>
          </SrpFileThumbnail>
        </li>
      </ul>

      <LinkWithIcon v-if="hiddenPhotoCount > 0" class="location-upload-row__show-all" isDottedUnderline iconInset="1px auto auto 2px" @click="isShowingAllPhotos = true">
        <template #icon><IconEmbedded name="caret-bottom_4" :size="14" /></template>
        Show all {{ photoCount.toLocaleString("en-US") }} photos
      </LinkWithIcon>
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useMultiselectWithShift } from "@composables/useMultiselectWithShift";
import { usePhotoGroupUpload } from "@composables/usePhotoGroupUpload";
import { rowLocationLabel } from "@logic/LocationUploadRows";
import { useToastsStore } from "@stores/toasts";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpCheckbox from "@components/ui/SrpCheckbox.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";
import LocationCategoryIcon from "@components/SocialLocations/LocationCategoryIcon.vue";
import UploadPhotoForm from "@views/CMS/UploadPhotoForm.vue";

// Types
import type { LocationUploadRow } from "@logic/LocationUploadRows";
import type { MultiselectItemIdAndStatus } from "@composables/useMultiselectWithShift";
import type { UploadedImage } from "@contracts/uploadedImage";

// The collapsed row shows this many photos; the last one carries the "+N" for the rest.
const MAX_PREVIEWS = 4;
// A big album would otherwise add up to 1,000 thumbnails to the page the moment it is opened.
const EXPANDED_PAGE_SIZE = 60;
// The form only needs somewhere to read images from; this row keeps its own list.
const NO_IMAGES: Array<UploadedImage> = [];

const props = withDefaults(
  defineProps<{
    row: LocationUploadRow;
    customerId: string;
    /** Saves a batch of uploaded photo ids to this row, creating its gallery first when it has none. */
    saveFunction: (assetIds: Array<string>) => Promise<void>;
    /** Only the checked ids that belong to this row. */
    checkedPhotoIds?: Array<string>;
    isSelectMode?: boolean;
    /** The album options and Move/Delete Photos are off while any row is uploading (see LocationUploadsSection). */
    areAlbumActionsDisabled?: boolean;
    isVideoUploadAllowed?: boolean;
  }>(),
  {
    checkedPhotoIds: () => [],
    isSelectMode: false,
    areAlbumActionsDisabled: false,
    isVideoUploadAllowed: false,
  }
);

const emit = defineEmits<{
  (e: "uploadStateChanged", isUploading: boolean): void;
  (e: "checkUncheckFile", photoId: string, isChecked: boolean): void;
  (e: "checkUncheckAll", isChecked: boolean): void;
  (e: "startSelecting"): void;
  (e: "stopSelecting"): void;
  (e: "editAlbum", withLocationPicker: boolean): void;
  (e: "deleteAlbum"): void;
}>();

const toastsStore = useToastsStore();

// Photos ====================================================================
// Uploaded this session and not yet in the gallery (the save runs in batches, a moment behind the upload).
const unsavedPhotoIds = ref<Array<string>>([]);

const displayPhotoIds = computed<Array<string>>(() => {
  const saved = new Set(props.row.photoIds);
  return [...unsavedPhotoIds.value.filter(photoId => !saved.has(photoId)), ...props.row.photoIds];
});
const photoCount = computed<number>(() => displayPhotoIds.value.length);

const previewPhotoIds = computed<Array<string>>(() => displayPhotoIds.value.slice(0, MAX_PREVIEWS));
// The "+N" sits on top of the last preview, so that photo counts as one of the N.
const morePreviewCount = computed<number>(() => (photoCount.value > MAX_PREVIEWS ? photoCount.value - (MAX_PREVIEWS - 1) : 0));

const isExpanded = ref<boolean>(false);
const isShowingAllPhotos = ref<boolean>(false);
const visiblePhotoIds = computed<Array<string>>(() => (isShowingAllPhotos.value ? displayPhotoIds.value : displayPhotoIds.value.slice(0, EXPANDED_PAGE_SIZE)));
const hiddenPhotoCount = computed<number>(() => photoCount.value - visiblePhotoIds.value.length);
const lightboxKey = computed<string>(() => `LocationUpload-${props.row.key}`);

// Upload ====================================================================
const uploadPhotoFormRef = ref<InstanceType<typeof UploadPhotoForm> | null>(null);
// The key never changes for a row, so it is safe to capture once.
const rowKey = props.row.key;
const uploadInputId = `location-upload-${rowKey}`;

// Retries of the same failing batch shouldn't each raise a toast.
let hasReportedSaveError = false;

async function saveUploadedPhotos(assetIds: Array<string>) {
  try {
    await props.saveFunction(assetIds);
    hasReportedSaveError = false;
  } catch (error: any) {
    if (!hasReportedSaveError) {
      hasReportedSaveError = true;
      const serverMessage = typeof error?.response?.data === "string" ? error.response.data : "";
      toastsStore.error({
        message: `Couldn't save photos to ${props.row.title}`,
        details: serverMessage || "We'll keep trying. If this doesn't clear up, refresh the page.",
        expiresInMs: 30000,
      });
    }
    // The queue retries the batch.
    throw error;
  }
}

const { isUploadingInProgress, areFileUploadsComplete, maxAssetsPerGroup, handleUploadingStarted, handleAssetUploaded, handleUploadProgressChange, checkAssetLimitBeforeUpload } = usePhotoGroupUpload({
  storageKey: `LocationUploadRow-${rowKey}`,
  saveFunction: saveUploadedPhotos,
  getCurrentAssetCount: () => photoCount.value,
  groupNoun: "album",
  getLogContext: () => `${props.customerId} groupId=${props.row.gallery?.id ?? "none"} locationId=${props.row.location?.id ?? "none"}`,
});

const canUpload = computed<boolean>(() => !props.isSelectMode && !isUploadingInProgress.value);

const uploadedFileCount = ref<number>(0);
const totalFileCount = ref<number>(0);

function handleImageUploaded(uploadedImage: UploadedImage) {
  unsavedPhotoIds.value.unshift(uploadedImage.serverId);
  handleAssetUploaded(uploadedImage.serverId);
}

function handleProgress(info: { isUploadInProgress: boolean; completedCount?: number; totalCount?: number }) {
  uploadedFileCount.value = info.completedCount ?? 0;
  totalFileCount.value = info.totalCount ?? 0;
  handleUploadProgressChange(info);
}

watch(isUploadingInProgress, isUploading => {
  emit("uploadStateChanged", isUploading);
  // Everything is saved, so the row's own list has caught up.
  if (!isUploading) unsavedPhotoIds.value = [];
});

onBeforeUnmount(() => {
  if (isUploadingInProgress.value) emit("uploadStateChanged", false);
});

function openFilePicker() {
  if (!canUpload.value) return;
  uploadPhotoFormRef.value?.clickUploadImage();
}

// Copy ======================================================================
const locationLabel = computed<string>(() => rowLocationLabel(props.row));
const albumDescription = computed<string>(() => props.row.gallery?.comment?.trim() ?? "");

const statusText = computed<string>(() => {
  if (isUploadingInProgress.value) {
    if (areFileUploadsComplete.value) return "Saving…";
    // The form counts finished files; show the one in flight.
    return totalFileCount.value > 0 ? `Uploading ${Math.min(uploadedFileCount.value + 1, totalFileCount.value)} of ${totalFileCount.value}…` : "Uploading…";
  }
  if (photoCount.value > 0) return `${photoCount.value.toLocaleString("en-US")} ${photoCount.value === 1 ? "photo" : "photos"}`;
  return props.row.location ? "Upload photos to promote" : "No photos yet";
});

// Album options (the gear) ==================================================
const canAddLocation = computed<boolean>(() => Boolean(props.row.gallery) && !props.row.location);
// Also off while selecting: deleting an album out from under a selection would leave photos checked that no longer exist.
const isAlbumMenuDisabled = computed<boolean>(() => props.areAlbumActionsDisabled || props.isSelectMode);

function chooseEditAlbum(withLocationPicker: boolean, close: () => void) {
  close();
  emit("editAlbum", withLocationPicker);
}

function chooseDeleteAlbum(close: () => void) {
  close();
  emit("deleteAlbum");
}

// Drag and drop =============================================================
const isDragOver = ref<boolean>(false);

function isFileDrag(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

function handleDragOver(event: DragEvent) {
  if (isFileDrag(event)) isDragOver.value = true;
}

function handleDragLeave(event: DragEvent) {
  // Only clear when leaving the row entirely, not when moving between its children.
  const target = event.currentTarget as HTMLElement;
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (!target || !relatedTarget || !target.contains(relatedTarget)) isDragOver.value = false;
}

function handleDrop(event: DragEvent) {
  isDragOver.value = false;
  if (!isFileDrag(event)) return;
  if (!canUpload.value) {
    toastsStore.warn({
      message: props.isSelectMode ? "Finish moving or deleting photos first" : `Still uploading to ${props.row.title}`,
      details: props.isSelectMode ? "" : "Add more once this upload finishes.",
      expiresInMs: 6000,
    });
    return;
  }
  uploadPhotoFormRef.value?.dragFile(event);
}

// Selection =================================================================
const { defineWhichItemsToCheckUncheck } = useMultiselectWithShift();

const isAllChecked = computed<boolean>(() => photoCount.value > 0 && props.checkedPhotoIds.length === photoCount.value);

function checkUncheckPhoto(event: Event, photoId: string) {
  const result: Array<MultiselectItemIdAndStatus> = defineWhichItemsToCheckUncheck(event, photoId, displayPhotoIds.value, props.checkedPhotoIds);
  result.forEach(r => emit("checkUncheckFile", r.id, r.isChecked));
}

function toggleSelectAll() {
  const shouldCheck = !isAllChecked.value;
  // "All" includes the photos behind "Show all", so reveal them rather than select something unseen.
  if (shouldCheck) isShowingAllPhotos.value = true;
  emit("checkUncheckAll", shouldCheck);
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// Location upload row ========================================================
.location-upload-row {
  position: relative;
  // Keeps everything layered inside the row beneath the page's sticky bars (the "+N" tile used to sit on top of them).
  z-index: 0;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
  background: #fff;
  list-style: none;

  &--drag-over {
    border: 1px dashed #058587;
    background: rgba(5, 133, 135, 0.05);
  }

  &__main {
    padding: 16px 18px;
    gap: 14px;
    display: flex;
    align-items: center;
  }

  &__album-avatar {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    color: #667085;
    background: #e4e7ec;
  }

  &__copy {
    min-width: 0;
    flex: 1;
    gap: 4px;
    display: grid;
  }

  &__name-row {
    gap: 8px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__name {
    min-width: 0;
    color: #102f41;
    font: 600 15px/20px $font-family;
    overflow-wrap: anywhere;
  }

  &__meta {
    min-height: 18px;
    gap: 4px 10px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    color: #667085;
    font: 400 13px/18px sans-serif;
  }

  &__location {
    gap: 4px;
    display: inline-flex;
    align-items: center;
  }

  // Same underline as the page's other small links (LinkWithIcon's dotted style), on a real button.
  &__link {
    padding: 0;
    border: 0;
    background: transparent;
    color: #118689;
    font: inherit;
    text-decoration: underline dashed rgba(17, 134, 137, 0.5);
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover {
      text-decoration: none;
    }

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: 2px;
      border-radius: 3px;
    }
  }

  &__album-menu {
    display: inline-flex;
    align-items: center;

    &--disabled :deep(.dropdown-wrapper__clickable-element) {
      opacity: 0.45;
      pointer-events: none;
    }
  }

  &__gear {
    width: 26px;
    height: 26px;
    padding: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border: 0;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #f2f4f7;
    }

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: 1px;
    }

    &:disabled {
      cursor: default;
    }
  }

  // Rendered in the page body by the dropdown, so it is styled on its own rather than under the row.
  &__album-menu-list {
    padding: 5px;

    button {
      width: 100%;
      padding: 8px 10px;
      display: flex;
      align-items: center;
      border: 0;
      border-radius: 4px;
      background: #fff;
      color: #28343b;
      font: 400 14px/18px sans-serif;
      text-align: left;
      white-space: nowrap;
      cursor: pointer;

      &:hover {
        background: #eef6f5;
      }

      &:focus-visible {
        outline: 2px solid #087f82;
        outline-offset: -2px;
      }
    }

    .location-upload-row__album-menu-delete {
      color: #806468;
    }
  }

  &__previews {
    margin: 0;
    padding: 0;
    gap: 5px;
    display: flex;
    flex-shrink: 0;
    list-style: none;
    cursor: pointer;
  }

  &__preview {
    width: 40px;
    height: 40px;
    position: relative;
    border-radius: 6px;
    overflow: hidden;
    list-style: none;
  }

  // The thumbnail defaults to 4:3; these tiles are square.
  &__preview &__preview-image {
    width: 100%;
    height: 100%;
    aspect-ratio: 1/1;
    pointer-events: none;
  }

  &__preview-more {
    position: absolute;
    inset: 0;
    // Above the thumbnail, which layers its own contents internally.
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #fff;
    background: rgba(16, 47, 65, 0.62);
    font: 600 12px/1 sans-serif;
  }

  &__drop-hint {
    gap: 6px;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    color: #98a2b3;
    font: 400 13px/18px sans-serif;
  }

  &--drag-over &__drop-hint {
    color: #05696b;
  }

  &__upload-button {
    flex-shrink: 0;
  }

  &__upload-form {
    display: none;
  }

  &__details {
    padding: 14px 18px 18px;
    border-top: 1px solid #e7e9ed;
  }

  &__actions {
    margin-bottom: 14px;
    gap: 10px 22px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__description {
    margin: 0 0 12px;
    color: #5b5b5b;
    font: 14px/19px sans-serif;
    white-space: pre-wrap;
  }

  &__thumbnails {
    margin: 0;
    padding: 0;
    gap: 10px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    list-style: none;
  }

  &__thumbnail {
    aspect-ratio: 4/3;
    position: relative;
    z-index: 0;
    border-radius: 6px;
    background: #d7d7d7;
    list-style: none;
  }

  &__show-all {
    margin-top: 14px;
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-upload-row {
    &__main {
      padding: 14px 12px;
      flex-wrap: wrap;
    }

    &__copy {
      flex-basis: calc(100% - 60px);
    }

    // No dragging on a phone, and the tiles leave no room for the name.
    &__previews,
    &__drop-hint {
      display: none;
    }

    &__upload-button {
      width: 100%;
    }

    &__details {
      padding: 12px;
    }

    &__thumbnails {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
}
</style>
